import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DayPickerRanges from '../DayPickerRanges';
import Dropdown from '../Dropdown';
import { I18nFormatter } from '../../utils/I18n/I18n';
import UserStore from '../../stores/UserStore';

const sameDate = (a, b) => {
    const am = moment(a);
    const bm = moment(b);
    return am.year() === bm.year()
        && am.month() === bm.month()
        && am.date() === bm.date();
}

const DatePicker = ({
    startDate = null,
    timezone: timezoneProp = null,
    endDate = null,
    onApply = () => {},
    callbackParams = [],
    className = '',
    closeOnDocumentClick = true,
    loading = false
}) => {

    // Applied date (from props)
    const [appliedDate, setAppliedDate] = useState({ startDate, endDate });
    // Temporary date (for display while picking)
    const [tempDate, setTempDate] = useState(null);
    const dropdownEl = useRef(null);
    const images = {
        calendar: require('shared_img/icons/calendar.svg'),
    };

    useEffect(() => {
        setAppliedDate({ startDate, endDate });
    }, [startDate, endDate]);

    const onApplyRef = useRef(onApply);
    useEffect(() => { onApplyRef.current = onApply; }, [onApply]);

    useEffect(() => {
        // convert currently selected dates to new timezone
        // compare by timezone name, not by utcOffset: offsets differ across DST
        // (e.g. a date before Oct 25 in Europe/Kiev is +03:00, today is +02:00),
        // which made this effect re-run forever
        if (timezoneProp && appliedDate.startDate && appliedDate.startDate.tz() !== timezoneProp) {
            const startDateNew = moment.tz({
                year: appliedDate.startDate.year(),
                month: appliedDate.startDate.month(),
                date: appliedDate.startDate.date(),
            }, timezoneProp).startOf('day');
            const endDateNew = moment.tz({
                year: appliedDate.endDate.year(),
                month: appliedDate.endDate.month(),
                date: appliedDate.endDate.date(),
            }, timezoneProp).endOf('day');

            const vals = {
                startDate: startDateNew,
                endDate: endDateNew,
            };
            setAppliedDate(vals);
            // onApplyRef.current(vals);
        }
    }, [timezoneProp, appliedDate]);

    const onUpdateRange = (newStartDate, newEndDate) => {
        const dateValue = {
            startDate: newStartDate,
            endDate: newEndDate
        };
        setAppliedDate(dateValue);
        setTempDate(null);
        onApply(dateValue);
        onCancelRange();
    };

    const onCancelRange = () => {
        setTempDate(null);
        if (dropdownEl.current && typeof dropdownEl.current.onToggle === 'function') {
            dropdownEl.current.onToggle();
        }
    };

    const onDateChange = (newStartDate, newEndDate) => {
        setTempDate({ startDate: newStartDate, endDate: newEndDate });
    };

    const displayDate = appliedDate;
    const pickerDate = tempDate || appliedDate;

    let timeZone = timezoneProp || UserStore.getValidTimeZone();
    let start = displayDate.startDate ? I18nFormatter.date(displayDate.startDate, timeZone) : '';
    let end = displayDate.endDate ? I18nFormatter.date(displayDate.endDate, timeZone) : '';
    let label = start || 'Select date';

    if (displayDate.endDate && !sameDate(displayDate.startDate, displayDate.endDate)) {
        label += ` - ${end}`;
    }

    return (
        <Dropdown
            ref={dropdownEl}
            overlay={'body'}
            isCloseDocument={closeOnDocumentClick}
            header={(
                <span className={className + ' bold'}>
                    {label}
                    <img src={images.calendar} alt=""/>
                </span>
            )}
        >
            <DayPickerRanges
                className={'dropdown-menu'}
                timezone={timeZone}
                from={pickerDate.startDate}
                to={pickerDate.endDate}
                onChange={onDateChange}
                onAction={onUpdateRange}
                onCancel={onCancelRange}
                loading={loading}
            />
        </Dropdown>
    );

};

DatePicker.propTypes = {
    callbackParams: PropTypes.array,
    t: PropTypes.object,
    closeOnDocumentClick: PropTypes.bool,
    loading: PropTypes.bool
};

export default DatePicker;
